// (2026-05-21) Ayuda contextual del menú lateral.
//
// Cada opción del menú tiene un icono "?" que abre este modal con una
// explicación clara y en lenguaje sencillo de qué hace o qué permite
// esa opción. El contenido está pensado para que CUALQUIER persona
// (no solo un contador) lo entienda.
//
// Estructura de cada entrada de MENU_HELP (clave = ruta `link.to`):
//   titulo     : nombre legible de la opción
//   icono      : clase FontAwesome (se reusa el icono del menú)
//   resumen    : una sola frase — "qué es" (se muestra resaltado)
//   detalle    : párrafo — "qué puedes hacer aquí"
//   porRol     : (opcional) nota adicional según el rol del usuario.
//                Clave = rol en minúscula; valor = texto de la nota.
//
// IMPORTANTE sobre roles: varias opciones son rutas DISTINTAS para
// roles distintos (ej. /empresas para admin/auditor vs /clients para
// operador), por lo que su contenido ya queda naturalmente separado.
// `porRol` se usa solo cuando la MISMA ruta se comporta distinto según
// quién la vea (ej. auditor = solo lectura).
import React, { useEffect } from "react";
import ReactDOM from "react-dom";

export const MENU_HELP = {
  // ─────────────── INICIO ───────────────
  "/dashboard": {
    titulo: "Panel de Control",
    icono: "fas fa-tv",
    resumen: "La pantalla de inicio del sistema.",
    detalle:
      "Muestra un resumen general de tu actividad: indicadores clave, " +
      "totales del periodo y accesos rápidos. Es el punto de partida cada " +
      "vez que ingresas a la plataforma.",
  },

  // ─────────── DOCUMENTOS LEGALES ───────────
  "/proformas": {
    titulo: "Proformas",
    icono: "fas fa-file-import",
    resumen: "Presupuestos o cotizaciones previas a la factura.",
    detalle:
      "Una proforma es una cotización formal que todavía NO tiene validez " +
      "fiscal. Aquí registras y gestionas proformas; cuando el cliente las " +
      "aprueba, puedes convertirlas en factura con un clic, sin volver a " +
      "cargar los datos.",
  },
  "/invoices": {
    titulo: "Facturas",
    icono: "fas fa-file-invoice",
    resumen: "Las facturas fiscales que emites a tus clientes.",
    detalle:
      "Aquí ves, creas y administras tus facturas. La factura es un " +
      "documento fiscal válido ante el SENIAT. Desde esta sección también " +
      "puedes descargar el PDF de cada factura y reenviarla por correo al " +
      "cliente.",
  },
  "/credit-note": {
    titulo: "Notas de Crédito",
    icono: "fas fa-file-invoice",
    resumen: "Documentos que disminuyen o anulan una factura.",
    detalle:
      "Una nota de crédito corrige una factura ya emitida cuando hay una " +
      "devolución de mercancía, un descuento posterior o un error. En la " +
      "práctica, REBAJA el monto que el cliente debía pagar.",
  },
  "/debit-note": {
    titulo: "Notas de Débito",
    icono: "fas fa-file-invoice",
    resumen: "Documentos que aumentan el monto de una factura.",
    detalle:
      "Una nota de débito agrega un cargo adicional a una factura ya " +
      "emitida — por ejemplo intereses por mora, fletes o ajustes a favor " +
      "del emisor. En la práctica, INCREMENTA lo que el cliente debe pagar.",
  },
  "/withholdings": {
    titulo: "Retenciones",
    icono: "fas fa-file-invoice",
    resumen: "Comprobantes de impuestos retenidos (IVA / ISLR).",
    detalle:
      "Una retención es la porción del impuesto que el cliente NO te paga " +
      "directamente, sino que entera al SENIAT en tu nombre. Aquí registras " +
      "y consultas esos comprobantes de retención.",
  },
  "/dispatch-guide": {
    titulo: "Guía de despacho",
    icono: "fas fa-file-invoice",
    resumen: "Documento que acompaña el traslado de mercancía.",
    detalle:
      "La guía de despacho respalda el movimiento físico de productos de " +
      "un lugar a otro. Incluye origen, destino, transportista y el detalle " +
      "de los bultos. Se genera a partir de una factura y solo despacha la " +
      "cantidad disponible (descontando lo ya devuelto).",
  },
  "/salesBook": {
    titulo: "Libro de ventas",
    icono: "fas fa-file-invoice",
    resumen: "El registro fiscal de todas las ventas de un periodo.",
    detalle:
      "El libro de ventas consolida facturas, notas de crédito, notas de " +
      "débito y retenciones de un periodo. Es un reporte obligatorio ante " +
      "el SENIAT. Puedes filtrarlo por periodo, exportarlo a Excel y " +
      "enviarlo por correo al destinatario correspondiente.",
    porRol: {
      admin:
        "Como administrador puedes seleccionar una empresa específica y " +
        "generar su libro de ventas, incluido el de sus terceros vinculados.",
      auditor:
        "Como auditor puedes consultar el libro de cualquier empresa, en " +
        "modo de solo lectura.",
    },
  },

  // ─────────────── CONFIGURACIÓN ───────────────
  "/empresas": {
    titulo: "Empresas",
    icono: "fas fa-users",
    resumen: "Todas las empresas afiliadas al servicio de facturación.",
    detalle:
      "Aquí ves el listado completo de empresas que usan la plataforma. " +
      "Cada empresa es un cliente del servicio: puedes consultar sus datos " +
      "fiscales, su configuración y si está habilitada como emisor de " +
      "facturas por cuenta de terceros.",
    porRol: {
      auditor:
        "Como auditor tu acceso es de SOLO LECTURA: puedes consultar la " +
        "información pero no crearla, modificarla ni eliminarla.",
    },
  },
  "/clients": {
    titulo: "Clientes",
    icono: "fas fa-users",
    resumen: "Las personas o empresas a las que tú les emites facturas.",
    detalle:
      "Aquí registras y administras a tus clientes — quienes reciben tus " +
      "facturas. Sus datos (RIF, razón social, dirección, tipo de " +
      "contribuyente) se cargan automáticamente al momento de facturar, " +
      "evitando que los escribas cada vez.",
  },
  "/terceros": {
    titulo: "Terceros",
    icono: "fas fa-user-friends",
    resumen: "Empresas a las que facturas por cuenta de ellas.",
    detalle:
      "Un tercero es una empresa que te delega la emisión de SUS facturas. " +
      "Aquí gestionas a esos terceros vinculados a tu empresa. Esta opción " +
      "solo aparece si tu empresa está habilitada como emisor.",
  },
  "/contracts": {
    titulo: "Contratos de Terceros",
    icono: "fas fa-file-contract",
    resumen: "Los contratos que habilitan facturar por cuenta de terceros.",
    detalle:
      "Antes de poder emitir facturas en nombre de un tercero, debe existir " +
      "un contrato aprobado que lo respalde legalmente. Aquí subes, revisas " +
      "y apruebas esos contratos.",
    porRol: {
      auditor: "Como auditor tu acceso es de solo lectura.",
    },
  },
  "/products": {
    titulo: "Producto / Servicio",
    icono: "fas fa-cubes",
    resumen: "El catálogo de lo que vendes.",
    detalle:
      "Aquí registras los productos y servicios que ofreces, cada uno con " +
      "su precio, impuesto aplicable, peso y volumen. Al facturar, los " +
      "seleccionas de este catálogo en lugar de escribirlos a mano.",
  },
  "/taxpayer": {
    titulo: "Tipos de Contribuyentes",
    icono: "fas fa-tags",
    resumen: "Las categorías fiscales de contribuyente.",
    detalle:
      "Define los tipos de contribuyente (Ordinario, Especial, etc.). Esta " +
      "clasificación determina cómo se calculan los impuestos y las " +
      "retenciones para cada cliente.",
  },
  "/taxes": {
    titulo: "Impuestos",
    icono: "fas fa-percent",
    resumen: "Las alícuotas (tasas) de impuesto disponibles.",
    detalle:
      "Configura las tasas de IVA y otros impuestos: general (16%), " +
      "reducida (8%), adicional o de lujo (31%) y exento. Estas tasas se " +
      "aplican a los productos al momento de facturar.",
  },
  "/config-withholdings": {
    titulo: "Retenciones (configuración)",
    icono: "fas fa-percent",
    resumen: "Las reglas con que el sistema calcula las retenciones.",
    detalle:
      "Define los porcentajes y reglas de retención de IVA e ISLR. A " +
      "diferencia de la sección 'Retenciones' de Documentos Legales (donde " +
      "registras comprobantes), aquí configuras CÓMO se calculan.",
  },
  "/config-exchange-rate-history": {
    titulo: "Tasas de cambio BCV",
    icono: "fa-solid fa-list-ul",
    resumen: "El historial de la tasa de cambio del BCV.",
    detalle:
      "Consulta y administra las tasas de cambio del Banco Central de " +
      "Venezuela. El sistema las usa para convertir montos entre bolívares " +
      "y divisas en facturas y reportes.",
  },
  "/config-payment-methods": {
    titulo: "Métodos de pago",
    icono: "fas fa-tags",
    resumen: "Las formas de pago que aceptas.",
    detalle:
      "Configura los métodos de pago (efectivo, transferencia, tarjeta, " +
      "pago móvil, etc.) que aparecen como opción al registrar el cobro de " +
      "una factura.",
  },
  "/auditlogs": {
    titulo: "Registro de auditoría",
    icono: "fa-solid fa-list-ul",
    resumen: "El historial de todo lo que ocurre en el sistema.",
    detalle:
      "Registra quién hizo qué y cuándo: creación y modificación de " +
      "documentos, inicios de sesión, cambios de configuración. Sirve para " +
      "control interno y trazabilidad ante una revisión.",
  },
  "/company-users": {
    titulo: "Usuarios",
    icono: "fas fa-users",
    resumen: "Las personas que pueden ingresar al sistema.",
    detalle:
      "Administra las cuentas de usuario y el rol de cada una. El rol " +
      "determina qué puede ver y hacer cada persona dentro de la " +
      "plataforma.",
    porRol: {
      admin:
        "Como administrador gestionas los usuarios de todas las empresas " +
        "afiliadas.",
      auditor: "Como auditor tu acceso es de solo lectura.",
    },
  },

  // ─────────────── COBRANZA ───────────────
  "/billing-rules": {
    titulo: "Reglas de cobro",
    icono: "fas fa-scale-balanced",
    resumen: "Cómo se calcula cuánto cobrarle a cada empresa afiliada.",
    detalle:
      "Define la forma en que se cobra el servicio de facturación a cada " +
      "empresa: una tarifa por cada factura emitida o una tarifa por lote, " +
      "según el volumen. Estas reglas alimentan el corte mensual de " +
      "cobranza.",
  },
  "/billing-history": {
    titulo: "Histórico de cobranza",
    icono: "fas fa-calendar-check",
    resumen: "El registro de los cortes de cobranza mensual.",
    detalle:
      "Muestra cada corte de cobranza ejecutado: cuánto se cobró a cada " +
      "empresa, cuántas facturas emitió en el periodo y el monto total. " +
      "Desde aquí también puedes estimar y generar un nuevo corte.",
  },

  // ─────────────── COMPRAS ───────────────
  "/dashboard/compras": {
    titulo: "Panel de Compras",
    icono: "fas fa-cart-shopping",
    resumen: "Resumen de tu actividad de compras (en Bs.).",
    detalle:
      "Muestra los indicadores clave de tus compras: total comprado, " +
      "crédito fiscal, notas de crédito/débito recibidas, compras por " +
      "período y los rankings de proveedores y productos más comprados. " +
      "Es el espejo del panel de Ventas, pero del lado de las compras.",
  },
  "/compras/proveedores": {
    titulo: "Proveedores",
    icono: "fas fa-truck",
    resumen: "Tu directorio de proveedores y categorías de gasto.",
    detalle:
      "Aquí registras y administras los proveedores a los que les compras " +
      "(razón social, RIF, condiciones de pago, retención por defecto) y " +
      "las categorías para clasificar tus gastos.",
  },
  "/compras/ordenes": {
    titulo: "Órdenes de Compra",
    icono: "fas fa-clipboard-list",
    resumen: "Solicitudes de compra a tus proveedores (no fiscal).",
    detalle:
      "Una orden de compra formaliza un pedido a un proveedor antes de que " +
      "te facture. Puedes aprobarla, enviarla, registrar la recepción y, " +
      "cuando llega la factura del proveedor, convertirla en una Compra.",
  },
  "/compras/facturas": {
    titulo: "Compras",
    icono: "fas fa-file-invoice-dollar",
    resumen: "Las facturas que tus proveedores te emiten.",
    detalle:
      "Registras aquí la factura de cada proveedor (capturando su nº de " +
      "control). El IVA se vuelve crédito fiscal. Al registrarla se calculan " +
      "las retenciones (IVA/ISLR) que le aplicas al proveedor y puedes " +
      "cargar los pagos (con IGTF si pagas en divisas).",
  },
  "/compras/gastos": {
    titulo: "Gastos",
    icono: "fas fa-receipt",
    resumen: "Egresos operativos clasificados por categoría.",
    detalle:
      "Para gastos con o sin soporte fiscal (servicios, viáticos, etc.). " +
      "Cada gasto se clasifica por categoría y puedes marcar si entra o no " +
      "al Libro de Compras. También registras sus pagos.",
  },
  "/compras/libro": {
    titulo: "Libro de Compras",
    icono: "fas fa-book",
    resumen: "El libro fiscal de IVA Compras (crédito fiscal).",
    detalle:
      "Reporte por período con el formato SENIAT: facturas, importaciones, " +
      "retenciones y el resumen de crédito fiscal. Puedes descargarlo en " +
      "Excel para tu declaración.",
  },
};

/**
 * Modal de ayuda contextual del menú.
 *
 * Props:
 *   help    : objeto de MENU_HELP (o null para no renderizar)
 *   rol     : rol del usuario en minúscula (para resolver porRol)
 *   onClose : callback al cerrar
 */
export function MenuHelpModal({ help, rol, onClose }) {
  // Cerrar con tecla Escape.
  useEffect(() => {
    if (!help) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [help, onClose]);

  if (!help) return null;

  const notaRol = help.porRol ? help.porRol[(rol || "").toLowerCase()] : null;

  return ReactDOM.createPortal(
    <div
      className="fixed inset-0 flex items-center justify-center p-4"
      style={{
        zIndex: 10000,
        background: "rgba(13,13,16,0.6)",
        backdropFilter: "blur(3px)",
      }}
      onClick={onClose}
    >
      <div
        className="bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
      >
        {/* HEADER */}
        <div
          className="px-6 py-4 flex items-center gap-3"
          style={{ background: "linear-gradient(135deg,#1A1A1A,#1C2267)" }}
        >
          <span className="shrink-0 w-11 h-11 rounded-xl bg-white/10 flex items-center justify-center text-white text-lg">
            <i className={help.icono} />
          </span>
          <div className="min-w-0">
            <h3 className="text-white font-bold text-lg leading-tight">
              {help.titulo}
            </h3>
            <p className="text-white/45 text-[0.7rem] uppercase tracking-wider">
              ¿Qué es esta opción?
            </p>
          </div>
          <button
            onClick={onClose}
            className="ml-auto text-white/60 hover:text-white transition"
            aria-label="Cerrar"
          >
            <i className="fas fa-times text-lg" />
          </button>
        </div>

        {/* BODY */}
        <div className="px-6 py-5">
          <p className="text-sm font-semibold text-indigo-800 bg-indigo-50 rounded-lg px-3 py-2 mb-3">
            {help.resumen}
          </p>
          <p className="text-sm text-slate-600 leading-relaxed">
            {help.detalle}
          </p>
          {notaRol && (
            <p className="text-sm text-amber-800 bg-amber-50 border-l-4 border-amber-300 rounded px-3 py-2 mt-3 leading-relaxed">
              <i className="fas fa-circle-info mr-1.5" />
              {notaRol}
            </p>
          )}
        </div>

        {/* FOOTER */}
        <div className="px-6 py-4 bg-slate-50 flex justify-end">
          <button
            onClick={onClose}
            className="bg-indigo-600 hover:bg-indigo-700 text-white font-semibold py-2 px-6 rounded-lg transition"
          >
            Entendido
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}

export default MenuHelpModal;
